import formatCurrency from '../../../../helpers/ConvertCurrencyToBRL';
import {
  Divider,
  PurchaseDetails,
  SectionTitle,
  SummaryCard,
  SummaryCouponRow,
  SummaryFooter,
  SummaryHeader,
  SummaryInfoIcon,
  SummaryItemDescription,
  SummaryItemPrice,
  SummaryItemQuantity,
  SummaryItemRow,
  SummaryOriginalPrice,
  SummaryPaymentTodayRow,
  SummarySection,
  SummarySectionLabel,
  SummaryTimeline,
  SummaryTotalRow,
} from '../styles';
import { IPaymentState, } from '../types/types';

interface PurchaseSummaryProps {
  paymentState: IPaymentState;
  children?: React.ReactNode;
}

interface PriceWithDiscountProps {
  original: number;
  final: number;
}

interface PriceRowProps extends PriceWithDiscountProps {
  name: string;
  quantity: number;
}

const PriceWithDiscount: React.FC<PriceWithDiscountProps> = ({
  original,
  final,
}) => (
  <>
    {final < original && (
      <SummaryOriginalPrice>{formatCurrency(original)}</SummaryOriginalPrice>
    )}
    {formatCurrency(final)}
  </>
);

const PriceRow: React.FC<PriceRowProps> = ({
  name,
  quantity,
  original,
  final,
}) => (
  <SummaryItemRow>
    <SummaryItemDescription>
      {name} |{' '}
      <SummaryItemQuantity>
        {quantity}x{' '}
        {quantity === 1
          ? 'item'
          : 'itens'}
      </SummaryItemQuantity>
    </SummaryItemDescription>

    <SummaryItemPrice>
      <PriceWithDiscount original={original} final={final} />
    </SummaryItemPrice>
  </SummaryItemRow>
);

export const PurchaseSummary: React.FC<PurchaseSummaryProps> = ({
  paymentState,
  children,
}) => {
  const plan = paymentState.plan;
  const addons = paymentState.addons ?? [];
  const discountAmount = paymentState.discountAmount;
  const totalAmount = paymentState.finalPrice;
  const hasCoupon = Boolean(paymentState.couponId);

  return (
    <PurchaseDetails>
      <SummaryCard>
        <SummaryHeader>
          <SectionTitle>Detalhes da compra</SectionTitle>

          <SummaryInfoIcon aria-label="Informações sobre a compra">i</SummaryInfoIcon>
        </SummaryHeader>

        <SummaryTimeline>
          {plan && (
            <SummarySection>
              <SummarySectionLabel>
                Plano
              </SummarySectionLabel>

              <PriceRow
                name={plan.name}
                quantity={1}
                original={plan.amount}
                final={plan.discountedAmount}
              />
            </SummarySection>
          )}
          {addons.length > 0 && (
            <SummarySection>
              <SummarySectionLabel>
                Add-ons
              </SummarySectionLabel>

              {addons.map(addon => (
                <PriceRow
                  key={addon.addon_id}
                  name={addon.name}
                  quantity={addon.item_count}
                  original={addon.total}
                  final={addon.discountedTotal}
                />
              ))}
            </SummarySection>
          )}
        </SummaryTimeline>

        {hasCoupon && (
          <>
            <Divider />

            <SummaryCouponRow>
              <span>
                {paymentState.couponCode
                  ? `Cupom ${paymentState.couponCode}`
                  : 'Cupom aplicado'}
              </span>

              {discountAmount > 0 && (
                <span>- {formatCurrency(discountAmount)}</span>
              )}
            </SummaryCouponRow>
          </>
        )}

        <Divider />

        <SummaryTotalRow>
          <span>Total</span>

          <span>
            <PriceWithDiscount
              original={paymentState.subtotal}
              final={totalAmount}
            />
          </span>
        </SummaryTotalRow>
      </SummaryCard>

      <SummaryFooter>
        <SummaryPaymentTodayRow>
          <span>Total a pagar hoje</span>

          <strong>
            {formatCurrency(totalAmount)}
          </strong>
        </SummaryPaymentTodayRow>

        {children}
      </SummaryFooter>
    </PurchaseDetails>
  );
};
